/**
 * The API client (#1312).
 *
 * One fetch wrapper, one token store. Paths are given WITHOUT the /api prefix
 * — the client adds it — so a caller cannot write a path that works in dev and
 * 404s behind Caddy, which is exactly what happened to /api/health on
 * 2026-08-26.
 *
 * ⚠️ The access token lives 15 minutes. Anything that does not refresh is an
 * app that logs you out mid-shop, so a 401 tries the refresh token once before
 * it gives up on the session.
 */

const TOKEN_KEY = "two-plates.token";
const REFRESH_KEY = "two-plates.refresh";
const NAME_KEY = "two-plates.name";

export interface Session {
  token: string;
  refreshToken?: string;
  name?: string;
}

/**
 * Where somebody goes when their session is genuinely gone, refresh included.
 *
 * ⛔ THE LANDING PAGE, NOT AN IN-APP SIGN-IN SCREEN. There is one sign-in
 * surface and it is the modal on / — building the modal and leaving a second
 * login route behind produced exactly the bug you would expect: the app kept
 * forwarding people to a screen that was no longer the front door.
 *
 * A full page load rather than a route change, because the landing is a
 * separate document.
 */
export function toSignIn(notice = "Your session ended. Sign in to pick up where you were.") {
  location.href = `/?notice=${encodeURIComponent(notice)}`;
}

export const session = {
  token: () => localStorage.getItem(TOKEN_KEY),
  name: () => localStorage.getItem(NAME_KEY) || undefined,
  signedIn: () => !!localStorage.getItem(TOKEN_KEY),
  save(s: Session) {
    localStorage.setItem(TOKEN_KEY, s.token);
    if (s.refreshToken) localStorage.setItem(REFRESH_KEY, s.refreshToken);
    if (s.name) localStorage.setItem(NAME_KEY, s.name);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(NAME_KEY);
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * 402 Payment Required — the one status code that means exactly this.
   * The message is already the upsell copy, written server-side so it cannot
   * drift from the rule that produced it.
   */
  get needsUpgrade(): boolean {
    return this.status === 402;
  }
}

/** Concurrent 401s share one refresh rather than racing four of them. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken?: string; refreshToken?: string };
      if (!body.accessToken) return false;
      session.save({ token: body.accessToken, refreshToken: body.refreshToken });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function send(path: string, init: RequestInit, auth: boolean): Promise<Response> {
  const token = session.token();
  const { headers, ...rest } = init;
  return fetch(`/api${path}`, {
    ...rest,
    headers: {
      ...(rest.body ? { "Content-Type": "application/json" } : {}),
      ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth = true, ...rest } = init;

  let res = await send(path, rest, auth);

  if (res.status === 401 && auth) {
    if (await refreshSession()) {
      res = await send(path, rest, auth);
    }
    if (res.status === 401) {
      session.clear();
      toSignIn();
      throw new ApiError(401, "Please sign in again");
    }
  }

  const text = await res.text();
  const body = text ? safeJson(text) : null;

  if (!res.ok) {
    // A 503 from this service means it cannot verify tokens at all. That is not
    // the caller's fault and must never read as "wrong password".
    const message =
      (body as any)?.error ||
      (res.status === 503 ? "Two Plates is having trouble — try again shortly" : res.statusText);
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/*
 * ⛔ There is no signIn() here, on purpose. The one sign-in surface is the
 * modal in index.html (9ef46e1), which carries the two-factor steps. A second
 * copy here had no caller and posted to /auth/login without
 * `secondFactor: "authenticator_first"` — reviving it would email a code to
 * every authenticator user. Sign in through the landing page (toSignIn()).
 */

export function signOut() {
  session.clear();
}
